import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { WorkflowNode, WorkflowConnection } from '../types/workflow';
import { Page, Section } from '../components/layout/primitives';
import { WorkflowBuilder, type WorkflowPodLink } from '../components/workflow/WorkflowBuilder';
import { WorkflowRunDrawer } from '../components/workflow/WorkflowRunDrawer';
import { WorkflowRunModal, type WorkflowRunFormValue } from '../components/workflow/WorkflowRunModal';
import { WorkflowTestPanel } from '../components/workflow/WorkflowTestPanel';
import { WorkflowConversationsDrawer } from '../components/workflow/WorkflowConversationsDrawer';
import { buildWorkflowDefinition } from '../lib/workflow/buildWorkflowDefinition';
import { parseWorkflowDefinition } from '../lib/workflow/parseWorkflowDefinition';
import { validateWorkflowDefinition } from '../lib/workflow/validateWorkflow';
import { workflowService } from '../services/workflowService';
import { Surface } from '../components/ui/Surface';

export const WorkflowEditor: React.FC = () => {
  const navigate = useNavigate();
  const { workflowId } = useParams<{ workflowId?: string }>();
  const isEditMode = Boolean(workflowId && workflowId !== 'new');

  const [loading, setLoading] = useState(isEditMode);
  const [initialName, setInitialName] = useState('Untitled Workflow');
  const [initialNodes, setInitialNodes] = useState<WorkflowNode[]>([]);
  const [initialConnections, setInitialConnections] = useState<WorkflowConnection[]>([]);
  const [initialStatus, setInitialStatus] = useState<'draft' | 'published' | 'archived' | 'paused'>('draft');
  const [podSubscription, setPodSubscription] = useState<WorkflowPodLink | null>(null);
  const [executionState, setExecutionState] = useState<{
    open: boolean;
    name: string;
    runId?: string;
  }>({
    open: false,
    name: '',
    runId: undefined,
  });
  const [runModalState, setRunModalState] = useState<{
    open: boolean;
    workflowName: string;
  }>({
    open: false,
    workflowName: '',
  });
  const [runRequestPending, setRunRequestPending] = useState(false);
  const [testPanelOpen, setTestPanelOpen] = useState(false);
  const [conversationsOpen, setConversationsOpen] = useState(false);

  const handleTestWorkflow = () => {
    if (!workflowId || workflowId === 'new') {
      toast.error('Save this workflow before testing it.');
      return;
    }
    setTestPanelOpen(true);
  };

  const handleConversations = () => {
    if (!workflowId || workflowId === 'new') {
      toast.error('Save this workflow first — conversations appear after it runs.');
      return;
    }
    setConversationsOpen(true);
  };

  useEffect(() => {
    if (!isEditMode || !workflowId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const definition = await workflowService.getById(workflowId);
        if (cancelled) return;
        const parsed = parseWorkflowDefinition(definition);
        if ((definition as any).status) setInitialStatus((definition as any).status);
        setPodSubscription(((definition as any).podSubscription as WorkflowPodLink | null) ?? null);
        setInitialName(parsed.name);
        setInitialNodes(parsed.nodes);
        setInitialConnections(parsed.connections);
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load workflow');
        navigate('/workflows');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, workflowId, navigate]);

  const handleSaveWorkflow = async (workflow: {
    id?: string;
    name: string;
    nodes: WorkflowNode[];
    connections: WorkflowConnection[];
    status: 'draft' | 'published' | 'archived' | 'paused';
  }) => {
    const definition = buildWorkflowDefinition({
      id: workflow.id,
      name: workflow.name,
      nodes: workflow.nodes,
      connections: workflow.connections,
      status: workflow.status,
    });
    const validation = validateWorkflowDefinition(definition);

    if (!validation.valid) {
      toast.error(
        `Workflow validation failed:\n${validation.issues.map((issue) => `- ${issue.message}`).join('\n')}`
      );
      return;
    }

    try {
      if (isEditMode && workflowId) {
        await workflowService.update(workflowId, definition);
        toast.success(
          workflow.status === 'published'
            ? `Workflow "${workflow.name}" published`
            : `Workflow "${workflow.name}" saved as draft`
        );
        // Stay on the editor after saving — don't navigate back to the list.
      } else {
        const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...payload } = definition;
        const created = await workflowService.create(payload);
        toast.success(
          workflow.status === 'published'
            ? `Workflow "${workflow.name}" published`
            : `Workflow "${workflow.name}" created as draft`
        );
        // A new workflow now has an id — move to its edit URL so subsequent saves
        // update it (rather than creating duplicates), while staying in the editor.
        if (created?.id) {
          navigate(`/workflows/${created.id}/edit`, { replace: true });
        } else {
          navigate('/workflows');
        }
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save workflow');
    }
  };

  const handleRunWorkflow = async (workflow: {
    name: string;
    nodes: WorkflowNode[];
    connections: WorkflowConnection[];
  }) => {
    if (!workflowId) {
      toast.error('Save this workflow before running it.');
      return;
    }

    const definition = buildWorkflowDefinition({
      id: workflowId,
      name: workflow.name,
      nodes: workflow.nodes,
      connections: workflow.connections,
    });
    const validation = validateWorkflowDefinition(definition);

    if (!validation.valid) {
      toast.error(
        `Workflow validation failed:\n${validation.issues.map((issue) => `- ${issue.message}`).join('\n')}`
      );
      return;
    }

    setRunModalState({
      open: true,
      workflowName: workflow.name,
    });
  };

  const handleSubmitRun = async (value: WorkflowRunFormValue) => {
    if (!workflowId) {
      toast.error('Save this workflow before running it.');
      return;
    }

    try {
      setRunRequestPending(true);
      const run = await workflowService.createRun(workflowId, value);
      setRunModalState({ open: false, workflowName: '' });
      setExecutionState({
        open: true,
        name: runModalState.workflowName,
        runId: run.id,
      });
      toast.success('Workflow run started');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to start workflow run');
    } finally {
      setRunRequestPending(false);
    }
  };

  if (loading) {
    return (
      <Page containerSize="wide" contentClassName="py-6">
        <Section className="py-4 md:py-6">
          <Surface className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600">
            Loading workflow...
          </Surface>
        </Section>
      </Page>
    );
  }

  return (
    <Page containerSize="wide" contentClassName="py-6">
      <Section className="py-4 md:py-6">
        <WorkflowBuilder
          key={isEditMode ? workflowId : 'new'}
          workflowId={isEditMode ? workflowId : undefined}
          initialName={initialName}
          initialNodes={initialNodes}
          initialConnections={initialConnections}
          initialStatus={initialStatus}
          podSubscription={podSubscription}
          onSave={handleSaveWorkflow}
          onRun={handleRunWorkflow}
          onTest={handleTestWorkflow}
          onConversations={handleConversations}
          onBack={() => navigate('/workflows')}
        />
      </Section>
      <WorkflowRunDrawer
        open={executionState.open}
        onClose={() => setExecutionState((prev) => ({ ...prev, open: false, runId: undefined }))}
        workflowId={workflowId}
        workflowName={executionState.name}
        runId={executionState.runId}
      />
      <WorkflowRunModal
        open={runModalState.open}
        workflowName={runModalState.workflowName}
        loading={runRequestPending}
        onClose={() => !runRequestPending && setRunModalState({ open: false, workflowName: '' })}
        onSubmit={handleSubmitRun}
      />
      <WorkflowTestPanel
        open={testPanelOpen}
        workflowId={workflowId && workflowId !== 'new' ? workflowId : undefined}
        workflowName={initialName}
        onClose={() => setTestPanelOpen(false)}
      />
      <WorkflowConversationsDrawer
        open={conversationsOpen}
        workflowId={workflowId && workflowId !== 'new' ? workflowId : undefined}
        workflowName={initialName}
        onClose={() => setConversationsOpen(false)}
      />
    </Page>
  );
};

export default WorkflowEditor;
